/**
 * Student payouts (Stripe Connect) — data layer.
 *
 *   GET  /api/connect/status?returned=1  → { eligible, state, payouts_ready, grants[] … }
 *   POST /api/connect/onboarding-link    → { url }  (Stripe-hosted onboarding, single-use)
 *   POST /api/connect/dashboard-link     → { url }  (Express dashboard login link)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { beginExternalNav, finishExternalNav } from "./externalNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

export const startPayoutOnboarding = async () => {
  const win = beginExternalNav();
  try {
    const res = await client.post("/connect/onboarding-link");
    finishExternalNav(win, res.data.url);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
};

export const openStripeDashboard = async () => {
  const win = beginExternalNav() || window.open("about:blank", "_blank");
  try {
    const res = await client.post("/connect/dashboard-link");
    finishExternalNav(win, res.data.url);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
};

export const postGrantThankYou = async (grantId, message) => {
  const res = await client.post(`/connect/grants/${grantId}/thank-you`, { message });
  return res.data;
};

export const usePayoutStatus = ({ enabled = true, returned = false } = {}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");

  const refresh = useCallback(async (opts = {}) => {
    if (!enabled) return;
    setLoading(true);
    setError("");
    try {
      const params = opts.returned ? { returned: true } : {};
      const res = await client.get("/connect/status", { params });
      setStatus(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load payout status.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { refresh({ returned }); }, [refresh, returned]);

  return { status, loading, error, refresh };
};
